import React, { useRef, useState } from 'react';
import { Spinner } from 'reactstrap';
import { useOrganizationStore } from '../../store/organization.store';
import OrgLogoChip from '../OrgLogoChip';

const LOGO_MAX_BYTES = 5 * 1024 * 1024; // 5 MB
const LOGO_TARGET_PX = 256;
const LOGO_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml'];

async function resizeImageToBlob(file: File, maxDim: number): Promise<Blob> {
  if (file.type === 'image/svg+xml') return file;
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error('Could not read image file'));
      el.src = url;
    });
    const ratio = Math.min(maxDim / img.naturalWidth, maxDim / img.naturalHeight, 1);
    const w = Math.max(1, Math.round(img.naturalWidth * ratio));
    const h = Math.max(1, Math.round(img.naturalHeight * ratio));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas not supported');
    ctx.drawImage(img, 0, 0, w, h);
    return await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Image conversion failed'));
      }, 'image/png');
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

interface Props {
  firebase: any;
}

const OrgLogoUploader: React.FC<Props> = ({ firebase }) => {
  const currentOrgId = useOrganizationStore((s) => s.currentOrgId);
  const currentOrgLogoUrl = useOrganizationStore((s) => s.currentOrgLogoUrl);
  const setCurrentOrgLogo = useOrganizationStore((s) => s.setCurrentOrgLogo);
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleFilePicked = async (file: File) => {
    if (!firebase || !currentOrgId) return;
    if (!LOGO_TYPES.includes(file.type)) {
      setError('Logo must be a PNG, JPEG, WebP, or SVG image.');
      return;
    }
    if (file.size > LOGO_MAX_BYTES) {
      setError(`Logo file is too large. Max ${(LOGO_MAX_BYTES / (1024 * 1024)).toFixed(0)} MB.`);
      return;
    }
    setError(null);
    setSuccess(null);
    setUploading(true);
    try {
      const isSvg = file.type === 'image/svg+xml';
      const blob = await resizeImageToBlob(file, LOGO_TARGET_PX);
      const ext = isSvg ? 'svg' : 'png';
      const otherExt = isSvg ? 'png' : 'svg';
      const contentType = isSvg ? 'image/svg+xml' : 'image/png';

      const { ref, uploadBytes, getDownloadURL, deleteObject } = await import('firebase/storage');
      const { doc, updateDoc } = await import('firebase/firestore');

      const targetRef = ref(firebase.storage, `organizations/${currentOrgId}/assets/logo.${ext}`);
      await uploadBytes(targetRef, blob, { contentType });
      const url = await getDownloadURL(targetRef);

      try {
        await deleteObject(ref(firebase.storage, `organizations/${currentOrgId}/assets/logo.${otherExt}`));
      } catch (_) {
        // Best-effort — likely just doesn't exist.
      }

      await updateDoc(doc(firebase.db, 'organizations', currentOrgId), { logoUrl: url });
      setCurrentOrgLogo(url);
      setSuccess('Logo updated.');
    } catch (err: any) {
      console.error('Logo upload failed:', err);
      setError(err?.message || 'Logo upload failed.');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleRemove = async () => {
    if (!firebase || !currentOrgId) return;
    setError(null);
    setSuccess(null);
    setRemoving(true);
    try {
      const { ref, deleteObject } = await import('firebase/storage');
      const { doc, updateDoc, deleteField } = await import('firebase/firestore');
      for (const ext of ['png', 'svg']) {
        try {
          await deleteObject(ref(firebase.storage, `organizations/${currentOrgId}/assets/logo.${ext}`));
        } catch (_) {
          // Best-effort.
        }
      }
      await updateDoc(doc(firebase.db, 'organizations', currentOrgId), { logoUrl: deleteField() });
      setCurrentOrgLogo(null);
      setSuccess('Logo removed.');
    } catch (err: any) {
      console.error('Logo removal failed:', err);
      setError(err?.message || 'Could not remove logo.');
    } finally {
      setRemoving(false);
    }
  };

  return (
    <div>
      <div className="d-flex align-items-center" style={{ gap: '1rem', marginBottom: '0.75rem' }}>
        <OrgLogoChip size={72} />
        <div>
          <div className="text-white" style={{ fontWeight: 500 }}>Organization logo</div>
          <div className="text-muted" style={{ fontSize: '0.85rem' }}>
            Shown in the navigation bar. Falls back to your organization's initials when no logo is set.
          </div>
        </div>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept={LOGO_TYPES.join(',')}
        style={{ display: 'none' }}
        onChange={(e) => {
          const f = e.target.files && e.target.files[0];
          if (f) handleFilePicked(f);
        }}
      />

      <div className="d-flex" style={{ gap: '0.5rem' }}>
        <button
          type="button"
          className="btn btn-sm btn-primary-custom btn-round"
          onClick={() => inputRef.current?.click()}
          disabled={uploading || removing}
        >
          <span>
            {uploading ? (
              <><Spinner size="sm" className="me-2" />Uploading…</>
            ) : currentOrgLogoUrl ? 'Replace logo' : 'Upload logo'}
          </span>
        </button>
        {currentOrgLogoUrl && (
          <button
            type="button"
            className="btn btn-sm btn-danger-custom btn-round"
            onClick={handleRemove}
            disabled={uploading || removing}
          >
            <span>{removing ? <Spinner size="sm" /> : 'Remove logo'}</span>
          </button>
        )}
      </div>

      {error && (
        <div style={{ color: 'var(--button-danger)', marginTop: '0.5rem', fontSize: '0.85rem' }}>
          {error}
        </div>
      )}
      {success && (
        <div className="text-success" style={{ marginTop: '0.5rem', fontSize: '0.85rem' }}>
          {success}
        </div>
      )}

      <div className="text-muted" style={{ fontSize: '0.8rem', marginTop: '0.75rem' }}>
        PNG, JPEG, WebP, or SVG up to {(LOGO_MAX_BYTES / (1024 * 1024)).toFixed(0)} MB.
        Raster images are resized to {LOGO_TARGET_PX}×{LOGO_TARGET_PX} on upload; SVGs are stored as-is.
      </div>
    </div>
  );
};

export default OrgLogoUploader;
